import { Plus, X, ArrowUp, ArrowDown, ListOrdered, RotateCcw } from "lucide-react";

export const RUNDOWN_KEY = "gawx_rundown";
export const loadRundown = () => { try { return JSON.parse(localStorage.getItem(RUNDOWN_KEY) || "[]"); } catch { return []; } };

/** Pick which graphics to show (left) and build an ordered rundown to click through (right). */
export function StudioRundown({ allSlides, rundown, setRundown, currentId, onShow }) {
  const byId = Object.fromEntries(allSlides.map((s) => [s.id, s]));
  const seq = rundown.filter((id) => byId[id]);
  const add = (id) => setRundown((r) => [...r, id]);
  const removeAt = (i) => setRundown((r) => r.filter((_, k) => k !== i));
  const swap = (i, j) => setRundown((r) => { if (j < 0 || j >= r.length) return r; const n = [...r]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const useLoop = () => setRundown(allSlides.map((s) => s.id));

  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="studio-rundown">
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="font-oswald text-sm font-bold uppercase tracking-wider text-white">Graphics ({allSlides.length})</div>
          <span className="font-oswald text-[11px] uppercase tracking-widest text-gray-500">Click to show · + adds to rundown</span>
        </div>
        <div className="grid max-h-72 grid-cols-1 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2">
          {allSlides.map((s) => (
            <div key={s.id} className={`flex items-center gap-1 rounded-md border px-2 py-1.5 ${currentId === s.id ? "border-[#1f63c9] bg-[#1f63c9]/20" : "border-white/10 bg-black/30 hover:bg-white/10"}`}>
              <button onClick={() => onShow(s.id)} data-testid={`studio-pick-${s.id}`} className="min-w-0 flex-1 truncate text-left font-oswald text-xs font-bold uppercase tracking-wide text-white">{s.title}</button>
              <button onClick={() => add(s.id)} data-testid={`studio-add-${s.id}`} title="Add to rundown" className="rounded p-1 text-gray-400 hover:bg-white/10 hover:text-white"><Plus className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-oswald text-sm font-bold uppercase tracking-wider text-white"><ListOrdered className="h-4 w-4 text-[#ffd60a]" /> My rundown ({seq.length})</div>
          <div className="flex items-center gap-2">
            <button onClick={useLoop} data-testid="studio-rundown-loop" className="flex items-center gap-1 rounded bg-white/10 px-2 py-1 font-oswald text-[11px] font-bold uppercase tracking-wider text-gray-200 hover:bg-white/20"><RotateCcw className="h-3 w-3" /> Use loop order</button>
            <button onClick={() => setRundown([])} disabled={!seq.length} data-testid="studio-rundown-clear" className="rounded bg-white/10 px-2 py-1 font-oswald text-[11px] font-bold uppercase tracking-wider text-gray-200 hover:bg-white/20 disabled:opacity-40">Clear</button>
          </div>
        </div>
        {seq.length ? (
          <ol className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
            {seq.map((id, i) => (
              <li key={`${id}-${i}`} className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${currentId === id ? "border-[#ffd60a] bg-[#ffd60a]/10" : "border-white/10 bg-black/30"}`} data-testid={`studio-step-${i}`}>
                <span className="w-6 text-center font-oswald text-xs font-black text-[#ffd60a]">{i + 1}</span>
                <button onClick={() => onShow(id)} className="min-w-0 flex-1 truncate text-left font-oswald text-xs font-bold uppercase tracking-wide text-white">{byId[id].title}</button>
                <button onClick={() => swap(i, i - 1)} className="rounded p-1 text-gray-400 hover:bg-white/10 hover:text-white"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button onClick={() => swap(i, i + 1)} className="rounded p-1 text-gray-400 hover:bg-white/10 hover:text-white"><ArrowDown className="h-3.5 w-3.5" /></button>
                <button onClick={() => removeAt(i)} data-testid={`studio-step-remove-${i}`} className="rounded p-1 text-gray-400 hover:bg-white/10 hover:text-[#2a74d8]"><X className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ol>
        ) : (
          <div className="rounded-md border border-dashed border-white/15 px-4 py-6 text-center font-oswald text-xs uppercase tracking-wider text-gray-500" data-testid="studio-rundown-empty">
            No rundown yet — add graphics with <Plus className="inline h-3 w-3" /> or use the loop order. Arrow keys and Next/Back then step through only your picks; number keys 1–9 jump to a step.
          </div>
        )}
      </div>
    </div>
  );
}

/** Compact step chips for the recording bar. */
export function RundownChips({ allSlides, seq, currentId, onShow }) {
  const byId = Object.fromEntries(allSlides.map((s) => [s.id, s]));
  if (!seq.length) return null;
  return (
    <div className="flex max-w-full flex-wrap items-center gap-1.5" data-testid="studio-rec-steps">
      {seq.map((id, i) => byId[id] && (
        <button key={`${id}-${i}`} onClick={() => onShow(id)} data-testid={`studio-rec-step-${i}`} className={`rounded px-2 py-1 font-oswald text-[11px] font-bold uppercase tracking-wider ${currentId === id ? "bg-[#ffd60a] text-[#0a0f1c]" : "bg-white/10 text-gray-200 hover:bg-white/20"}`}>
          {i + 1}. {byId[id].title}
        </button>
      ))}
    </div>
  );
}
